/**
 * Funções utilitárias para manipulação de telefones
 * Sistema simplificado - formato RAW único
 */

/**
 * Remove todos os caracteres não-numéricos de uma string
 */
export function onlyDigits(value: string = ''): string {
  // Garantir que value é string antes de chamar .replace()
  if (typeof value !== 'string') {
    return '';
  }
  return value.replace(/\D+/g, '');
}

export function normalizePhoneCountryBR(phone: string): string {
  const digits = onlyDigits(phone);
  return digits.length === 10 || digits.length === 11 ? `55${digits}` : digits;
}

/**
 * ✅ NORMALIZAÇÃO RAW - Formato único para todo o sistema
 * SEMPRE retorna: 55 + DDD + número completo (celular: 11 dígitos COM o 9)
 * 
 * FORMATO DE NÚMEROS BRASILEIRO:
 * - Código país: 55
 * - DDD: 2 dígitos (ex: 51)
 * - Celular: 9 dígitos começando com 9 (ex: 996495083)
 * - Total: 13 dígitos (5551996495083)
 * 
 * ❌ NUNCA remover o 9 inicial do número celular!
 * 
 * Exemplos:
 * - "51 996495083" → "5551996495083" (celular: 13 dígitos)
 * - "51996495083" → "5551996495083" (mantém)
 * - "5551996495083" → "5551996495083" (já correto)
 * - "48 88372424" → "554888372424" (fixo: 12 dígitos)
 * 
 * @returns 13 dígitos para celular (55 + 11) ou 12 para fixo (55 + 10)
 */
export function normalizePhoneRAW(phone: string): string {
  const digits = onlyDigits(phone);
  
  // 1. Adicionar 55 se não tiver
  const withCountry = normalizePhoneCountryBR(digits);
  
  // 2. Separar código do país e número local
  const countryCode = withCountry.substring(0, 2); // "55"
  let localNumber = withCountry.substring(2);      // "51996495083" ou "5196495083"
  
  // 3. ✅ CORREÇÃO: Garantir que celular tenha 11 dígitos (DDD + 9 dígitos)
  if (localNumber.length === 10) {
    // Se tem 10 dígitos e o terceiro é 9/8/7, adicionar 9 no início
    const ddd = localNumber.substring(0, 2);
    const firstDigit = localNumber.charAt(2);
    if (['9', '8', '7', '6'].includes(firstDigit)) {
      const numero = localNumber.substring(2);
      localNumber = `${ddd}9${numero}`; // Adiciona 9: "5196495083" → "51996495083"
    }
  }
  
  // 4. Resultado final: 55 + 11 dígitos (celular) ou 55 + 10 dígitos (fixo)
  return `${countryCode}${localNumber}`;  // "5551996495083"
}

/**
 * Adiciona o dígito 9 para formato brasileiro de exibição
 * 555196087088 → 5551996087088
 */
export function adicionarDigito9(numeroRAW: string): string {
  const digits = onlyDigits(numeroRAW);
  
  // Formato esperado: 55 + DDD (2) + número (8) = 12 dígitos
  if (digits.length !== 12 || !digits.startsWith('55')) {
    return digits; // Retorna como está se não for o formato esperado
  }
  
  const pais = digits.substring(0, 2);   // "55"
  const ddd = digits.substring(2, 4);    // "51"
  const numero = digits.substring(4);    // "96087088"
  
  // Inserir 9 após o DDD
  return `${pais}${ddd}9${numero}`;      // "5551996087088"
}

/**
 * Remove o dígito 9 para formato WhatsApp (RAW)
 * 5551996087088 → 555196087088
 */
export function removerDigito9(numeroComNove: string): string {
  const digits = onlyDigits(numeroComNove);
  
  // Formato esperado: 55 + DDD (2) + 9 + número (8) = 13 dígitos
  if (digits.length !== 13 || !digits.startsWith('55')) {
    return digits;
  }
  
  const pais = digits.substring(0, 2);   // "55"
  const ddd = digits.substring(2, 4);    // "51"
  // Pular o 9 (posição 4)
  const numero = digits.substring(5);    // "96087088"
  
  return `${pais}${ddd}${numero}`;       // "555196087088"
}

/**
 * Verifica se um número é celular (9XXXX-XXXX)
 * @returns true se celular, false se fixo
 */
export function isCelular(phone: string): boolean {
  const digits = onlyDigits(phone);
  
  // Remover código do país se tiver
  let localNumber = digits;
  if (digits.startsWith('55') && digits.length >= 12) {
    localNumber = digits.substring(2); // Remove "55"
  }
  
  // Se tem menos de 10 dígitos, não é válido
  if (localNumber.length < 10) {
    return false;
  }
  
  // O 3º dígito (primeiro após DDD) define o tipo
  const firstDigit = localNumber.charAt(2);
  
  // Celular: 9, 8, 7
  // Fixo: 3, 2, 4, 5
  return ['9', '8', '7'].includes(firstDigit);
}

/**
 * Formata telefone para exibição visual
 * Aceita formato RAW (12 dígitos) ou COM 9 (13 dígitos)
 * @returns Formato: (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
 */
export function formatPhone(phone: string): string {
  if (!phone) return '';
  
  const digits = onlyDigits(phone);
  
  // ✅ CORREÇÃO: Só adicionar 9 se for CELULAR
  let numeroParaFormatar = digits;
  if (digits.length === 12 && digits.startsWith('55')) {
    // Verificar se é celular antes de adicionar o 9
    if (isCelular(digits)) {
      numeroParaFormatar = adicionarDigito9(digits);
    }
    // Se for FIXO, mantém os 12 dígitos sem adicionar 9
  }
  
  // Remover código do país para formatação
  let localNumber = numeroParaFormatar;
  if (numeroParaFormatar.startsWith('55') && numeroParaFormatar.length >= 12) {
    localNumber = numeroParaFormatar.substring(2);
  }
  
  // Formatar
  if (localNumber.length === 11) {
    // Celular: (XX) 9XXXX-XXXX
    return localNumber.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  }
  
  if (localNumber.length === 10) {
    // Fixo: (XX) XXXX-XXXX
    return localNumber.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }
  
  return phone;
}

/**
 * Formata CNPJ para exibição (XX.XXX.XXX/XXXX-XX)
 */
export function formatCNPJ(cnpj: string): string {
  const digits = onlyDigits(cnpj);
  if (digits.length !== 14) return cnpj;
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

/**
 * Formata telefone para uso em links tel: (click-to-call/softphone)
 * Remove código do país e retorna apenas DDD + número
 * @returns Formato para softphone: DDD + número (10-11 dígitos)
 */
export function formatPhoneForCall(phone: string): string {
  if (!phone || typeof phone !== 'string') return '';
  
  const digits = onlyDigits(phone);
  if (!digits) return '';
  
  // Remover código do país 55 se presente (RAW format: 5551996495083)
  let localNumber = digits;
  if (digits.startsWith('55') && digits.length >= 12) {
    localNumber = digits.substring(2);
  }
  
  // ✅ CRÍTICO: Remover zeros iniciais que podem vir de formatação errada
  // Ex: "034418744" (do formato "(54) 03441-8744") → "34418744"
  while (localNumber.length > 10 && localNumber.startsWith('0')) {
    localNumber = localNumber.substring(1);
  }
  
  // Garantir que celular tenha o 9 (para ligações locais)
  // Celular: DDD + 9 dígitos = 11 dígitos
  // Fixo: DDD + 8 dígitos = 10 dígitos
  if (localNumber.length === 10 && isCelular(`55${localNumber}`)) {
    const ddd = localNumber.substring(0, 2);
    const numero = localNumber.substring(2);
    localNumber = `${ddd}9${numero}`;
  }
  
  return localNumber;
}

/**
 * Copia texto para clipboard e retorna Promise<boolean>
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Falha ao copiar:', err);
    return false;
  }
}
